import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { supabase } from '../lib/supabaseClient';

/**
 * AcceptInvite Page
 * 
 * Handles the invite flow when a user clicks the invite link from their email.
 * Allows them to set their password and complete account setup.
 */
const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout>;
    
    // Set up auth state listener FIRST, before checking session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isMounted) return;
        
        console.log('🔄 Auth state changed:', event, newSession?.user?.email);
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          if (newSession?.user) {
            console.log('✅ Session available for:', newSession.user.email);
            setUserEmail(newSession.user.email || '');
            setIsLoading(false);
            if (timeoutId) clearTimeout(timeoutId);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('❌ User signed out');
          setError('Invalid or expired invite link. Please request a new invitation.');
          setIsLoading(false);
        }
      }
    );
    
    // Check if we have a valid session from the invite link
    const checkSession = async () => {
      try {
        console.log('🔐 Checking invite session...');
        console.log('🔐 Current URL:', window.location.href);
        console.log('🔐 Hash:', window.location.hash);
        
        // Check for tokens in the URL hash and manually set session if needed
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
          console.log('🔐 Found access_token in hash, attempting to set session...');
          
          // Parse the hash to extract tokens
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          
          if (accessToken && refreshToken) {
            console.log('🔐 Setting session from URL tokens...');
            
            // Manually set the session with the tokens from the URL
            const { data, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (setSessionError) {
              console.error('❌ Error setting session:', setSessionError);
              // Check if it's an expired token error - user might already have an account
              if (setSessionError.message?.includes('expired') || setSessionError.message?.includes('invalid')) {
                setError('This invite link has expired. If you already have an account, please use the Login page instead. Otherwise, request a new invitation.');
              } else {
                setError('Invalid or expired invite link. Please request a new invitation.');
              }
              setIsLoading(false);
              return;
            }
            
            if (data.session?.user) {
              console.log('✅ Session set successfully for:', data.session.user.email);
              setUserEmail(data.session.user.email || '');
              setIsLoading(false);
              
              // Clean up the URL hash
              window.history.replaceState(null, '', window.location.pathname);
              return;
            }
          }
        }
        
        // Get current session (fallback)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (sessionError) {
          console.error('❌ Session error:', sessionError);
          setError('This invite link has expired. If you already have an account, please use the Login page instead. Otherwise, request a new invitation.');
          setIsLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Valid invite session for:', session.user.email);
          setUserEmail(session.user.email || '');
          setIsLoading(false);
        } else {
          // No session yet - wait for auth state change
          console.log('⏳ No session yet, waiting for auth state change...');
          
          // Timeout after 10 seconds
          timeoutId = setTimeout(() => {
            if (isMounted && isLoading) {
              console.log('❌ Timeout waiting for session');
              setError('This invite link has expired. If you already have an account, please use the Login page instead. Otherwise, request a new invitation.');
              setIsLoading(false);
            }
          }, 10000);
        }
      } catch (err) {
        console.error('❌ Error checking session:', err);
        if (isMounted) {
          setError('An error occurred. Please try again.');
          setIsLoading(false);
        }
      }
    };

    checkSession();
    
    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.password,
      });

      if (updateError) {
        console.error('❌ Password update error:', updateError);
        throw new Error(updateError.message);
      }

      console.log('✅ Password set successfully');
      setSuccess(true);

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">Verifying your invitation...</Typography>
        </Paper>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" gutterBottom color="success.main">
            ✅ Account Setup Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your password has been set. Redirecting to dashboard...
          </Typography>
          <CircularProgress size={24} sx={{ mt: 2 }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
          }}
        >
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="Check Set Go"
              sx={{ height: 80, mb: 2 }}
              onError={(e: any) => {
                e.target.style.display = 'none';
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                color: '#212121',
                letterSpacing: '2px',
              }}
            >
              CHECK SET GO
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#E53935',
                fontWeight: 600,
                letterSpacing: '1px',
                mt: 0.5,
              }}
            >
              FROM AUDIT TO ACTION
            </Typography>
          </Box>

          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 1 }}>
            Complete Your Account
          </Typography>
          
          {userEmail && (
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Setting up account for: <strong>{userEmail}</strong>
            </Typography>
          )}

          {error && (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              
              {/* Show login button if error suggests account already exists */}
              {error.includes('already have an account') && (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Your account is already active!
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      This invitation link has already been used. Please click the button below to login with your existing password.
                    </Typography>
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/login')}
                    size="large"
                    sx={{
                      bgcolor: '#E53935',
                      '&:hover': { bgcolor: '#C62828' },
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    GO TO LOGIN
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Forgot your password? Use the "Forgot Password" link on the login page.
                  </Typography>
                </Box>
              )}
            </>
          )}

          {(!error || formData.password) && (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                autoFocus
                helperText="Must be at least 8 characters"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                  },
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                margin="normal"
                required
                error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                helperText={
                  formData.confirmPassword !== '' && formData.password !== formData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !formData.password || formData.password !== formData.confirmPassword}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Set Password & Continue'
                )}
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AcceptInvite;

